/**
 * PropertyPage — household property + mortgage dashboard
 *
 * Surfaces gross value, mortgages, net equity, monthly cashflow, LTV per
 * property, and any urgent items (e.g. an empty buy-to-let bleeding
 * interest while waiting to sell).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Home, AlertTriangle, RefreshCw, Calendar, TrendingDown,
  Pencil, ExternalLink, Plus, Trash2, Save, X,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api, getPropertyPlans } from '@/lib/api'
import { usePlanningAssumptions } from '@/hooks/usePlanningAssumptions'
import type { PropertySalePlanEnriched } from '@/lib/types'

interface Mortgage {
  id: number
  name: string
  linked_property: string | null
  outstanding_gbp: number
  rate_pct: number
  fixed_until: string | null
  months_to_remortgage: number | null
  monthly_payment_contracted_gbp: number
  monthly_payment_actual_gbp: number
  annual_payment_gbp: number
  annual_interest_gbp: number
  type: string
  urgent_note: string | null
}

interface Property {
  id: number
  name: string
  gross_value_gbp: number
  owner: string
  notes: string | null
  mortgage_outstanding_gbp: number
  net_equity_gbp: number
  ltv_pct: number
  monthly_payment_gbp: number
  annual_interest_gbp: number
  linked_mortgages: Mortgage[]
  urgent_note: string | null
}

interface PropertySummary {
  totals: {
    gross_value_gbp: number
    mortgages_outstanding_gbp: number
    net_equity_gbp: number
    ltv_pct: number
    monthly_outflow_gbp: number
    annual_outflow_gbp: number
    annual_interest_gbp: number
    annual_principal_paid_gbp: number
  }
  properties: Property[]
  mortgages: Mortgage[]
  urgent_items: {
    type: string
    severity: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL'
    title: string
    message: string
    monthly_cost_gbp: number
    annual_cost_gbp: number
  }[]
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtDecimal = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`

export default function PropertyPage() {
  const [data, setData] = useState<PropertySummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Phase A — sale plans live in household.planning_assumptions.property_plans
  // and arrive enriched with current gross/mortgage/equity from
  // /api/property/plans. Edits go through usePlanningAssumptions.patch.
  const [plans, setPlans] = useState<PropertySalePlanEnriched[]>([])
  const patchAssumptions = usePlanningAssumptions((s) => s.patch)

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [summary, planRes] = await Promise.all([
        api.get<PropertySummary>('/api/property/summary'),
        getPropertyPlans().catch(() => ({ plans: [] })),
      ])
      setData(summary.data)
      setPlans(planRes.plans || [])
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load property data')
    } finally {
      setLoading(false)
    }
  }

  // Build slug → plan lookup so each property card can render its plan inline.
  const planForProperty = (propertyName: string): PropertySalePlanEnriched | undefined => {
    // Match by label substring or by holding_id slug embedded in the name.
    return plans.find(p =>
      (p.label && propertyName.toLowerCase().includes(p.label.toLowerCase())) ||
      (p.holding_id && propertyName.toLowerCase().includes(p.holding_id.toLowerCase().replace(/_/g, ' '))),
    )
  }

  // Replace one entry in property_plans (or remove it if `next` is null).
  // The PATCH endpoint expects the FULL list, so we splice locally and send.
  const upsertPlan = async (
    holding_id: string,
    label: string,
    next: { sell_year: number | null; net_proceeds_gbp: number | null } | null,
  ) => {
    const existing = plans.filter(p => p.holding_id !== holding_id)
    const merged = next === null
      ? existing
      : [...existing, {
          holding_id, label,
          sell_year: next.sell_year,
          net_proceeds_gbp: next.net_proceeds_gbp,
          include_in_fire: false,
        }]
    // Strip enrichment fields before sending — backend only stores the
    // canonical PropertySalePlan shape.
    const wire = merged.map(p => ({
      holding_id: p.holding_id,
      label: p.label,
      sell_year: p.sell_year,
      net_proceeds_gbp: p.net_proceeds_gbp,
      include_in_fire: p.include_in_fire,
    }))
    await patchAssumptions({ property_plans: wire } as any)
    await fetchData()
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-64 skeleton" />
        <div className="h-32 skeleton" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button type="button" onClick={fetchData} className="btn-secondary mt-3">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Home size={20} className="text-blue-light" aria-hidden />
            Property &amp; Mortgages
          </h1>
          <p className="text-sm text-text-secondary">
            Real estate, mortgages, equity, and monthly cashflow
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {/* Urgent items */}
      {data.urgent_items.length > 0 && (
        <div className="space-y-2">
          {data.urgent_items.map((u, i) => (
            <div
              key={i}
              className={clsx(
                'card p-4 border-2 flex items-start gap-3',
                u.severity === 'HIGH' || u.severity === 'CRITICAL'
                  ? 'border-rose/40 bg-rose/5'
                  : 'border-amber/40 bg-amber/5',
              )}
            >
              <AlertTriangle
                size={18}
                className={u.severity === 'HIGH' || u.severity === 'CRITICAL' ? 'text-rose-light' : 'text-amber'}
                aria-hidden
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-text-primary">{u.title}</p>
                <p className="text-xs text-text-secondary mt-1">{u.message}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-rose-light">−{fmt(u.monthly_cost_gbp)}/mo</p>
                <p className="text-[10px] text-text-muted">−{fmt(u.annual_cost_gbp)}/yr</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Headline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
        <div className="stat-card card-hover">
          <p className="stat-label">Gross Value</p>
          <p className="stat-value">{fmt(data.totals.gross_value_gbp)}</p>
          <p className="text-xs text-text-muted">{data.properties.length} propert{data.properties.length === 1 ? 'y' : 'ies'}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Mortgages</p>
          <p className="stat-value text-rose-light">−{fmt(data.totals.mortgages_outstanding_gbp)}</p>
          <p className="text-xs text-text-muted">LTV {data.totals.ltv_pct}%</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Net Equity</p>
          <p className="stat-hero text-emerald-light">{fmt(data.totals.net_equity_gbp)}</p>
          <p className="text-xs text-text-muted">your share</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Monthly Outflow</p>
          <p className="stat-value">{fmt(data.totals.monthly_outflow_gbp)}</p>
          <p className="text-xs text-text-muted">{fmt(data.totals.annual_interest_gbp)}/yr interest</p>
        </div>
      </div>

      {/* Per-property cards */}
      <section>
        <h2 className="panel-title mb-3">Properties</h2>
        <div className="space-y-3">
          {data.properties.map(p => (
            <div key={p.id} className="card p-5">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <h3 className="text-base font-bold text-text-primary">{p.name}</h3>
                  <p className="text-xs text-text-muted">
                    Owner: {p.owner}
                    {p.notes && ` · ${p.notes}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right shrink-0">
                    <p className="stat-value text-emerald-light">{fmt(p.net_equity_gbp)}</p>
                    <p className="text-[10px] text-text-muted">net equity</p>
                  </div>
                  {/* Edit property details — value, mortgage, name — at the
                      account-holding source of truth. */}
                  <Link
                    to="/app/accounts"
                    title="Edit property details (value, owner, notes) on the Accounts page"
                    className="shrink-0 p-2 rounded-lg border border-border text-text-muted hover:text-text-primary hover:bg-surface-card flex items-center gap-1 text-xs"
                  >
                    <Pencil size={12} aria-hidden />
                    <span className="hidden sm:inline">Edit</span>
                    <ExternalLink size={10} aria-hidden />
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-text-muted">Gross value</p>
                  <p className="font-bold text-text-primary">{fmt(p.gross_value_gbp)}</p>
                </div>
                <div>
                  <p className="text-text-muted">Mortgage</p>
                  <p className="font-bold text-rose-light">−{fmt(p.mortgage_outstanding_gbp)}</p>
                </div>
                <div>
                  <p className="text-text-muted">LTV</p>
                  <p className="font-bold text-text-primary">{p.ltv_pct.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-text-muted">Monthly</p>
                  <p className="font-bold text-text-primary">{fmt(p.monthly_payment_gbp)}</p>
                </div>
              </div>

              {/* Phase A — sale plan editor (links into MC / FIRE / drawdown) */}
              <SalePlanRow
                property={p}
                plan={planForProperty(p.name)}
                onSave={upsertPlan}
              />

              {p.linked_mortgages.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border space-y-2">
                  {p.linked_mortgages.map(m => (
                    <div key={m.id} className="bg-surface/50 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-text-primary">{m.name}</p>
                          <div className="flex items-center gap-3 text-[10px] text-text-muted mt-1 flex-wrap">
                            <span className="badge badge-blue text-[10px]">{m.type}</span>
                            <span>{m.rate_pct}% fixed</span>
                            {m.fixed_until && (
                              <span className="flex items-center gap-1">
                                <Calendar size={10} aria-hidden />
                                until {new Date(m.fixed_until).toLocaleDateString('en-GB')}
                                {m.months_to_remortgage !== null && (
                                  <span className={clsx(
                                    'ml-1 font-semibold',
                                    m.months_to_remortgage < 6 ? 'text-rose-light'
                                      : m.months_to_remortgage < 12 ? 'text-amber'
                                      : 'text-text-secondary',
                                  )}>
                                    ({m.months_to_remortgage}mo)
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0 text-[10px]">
                          <p className="text-text-muted">Contracted: {fmtDecimal(m.monthly_payment_contracted_gbp)}/mo</p>
                          {m.monthly_payment_actual_gbp !== m.monthly_payment_contracted_gbp && (
                            <p className="text-emerald-light">
                              Actual: {fmtDecimal(m.monthly_payment_actual_gbp)}/mo (overpaying)
                            </p>
                          )}
                          <p className="text-rose-light flex items-center gap-1 justify-end mt-0.5">
                            <TrendingDown size={9} aria-hidden />
                            {fmt(m.annual_interest_gbp)}/yr interest
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────────────────
// SalePlanRow — inline editor for the per-property `property_plans` entry.
// Reads the current plan (or empty), lets the user set sell year + net
// proceeds, and patches via PATCH /api/profile/assumptions. Edits flow
// through to MC / FIRE / drawdown / scenarios automatically.
// ─────────────────────────────────────────────────────────────────────────────

function SalePlanRow({
  property,
  plan,
  onSave,
}: {
  property: Property
  plan: PropertySalePlanEnriched | undefined
  onSave: (
    holding_id: string,
    label: string,
    next: { sell_year: number | null; net_proceeds_gbp: number | null } | null,
  ) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [year, setYear] = useState<number | ''>(plan?.sell_year ?? new Date().getFullYear())
  const [proceeds, setProceeds] = useState<number>(
    plan?.net_proceeds_gbp ?? Math.max(0, property.net_equity_gbp),
  )
  const [saving, setSaving] = useState(false)

  // Slug derivation: prefer existing plan's slug; otherwise derive from name.
  const derivedSlug = property.name.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `property_${property.id}`
  const slug = plan?.holding_id ?? derivedSlug
  const label = plan?.label ?? property.name

  const startEdit = () => {
    setYear(plan?.sell_year ?? new Date().getFullYear() + 1)
    setProceeds(plan?.net_proceeds_gbp ?? Math.max(0, property.net_equity_gbp))
    setEditing(true)
  }

  const cancel = () => setEditing(false)

  const save = async () => {
    setSaving(true)
    try {
      await onSave(slug, label, {
        sell_year: year === '' ? null : Number(year),
        net_proceeds_gbp: proceeds || null,
      })
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!plan) return
    if (!window.confirm(`Remove sale plan for ${label}? FIRE / MC / drawdown will stop modelling this lump.`)) return
    setSaving(true)
    try {
      await onSave(plan.holding_id, plan.label, null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-4 pt-3 border-t border-border">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="panel-title">Sale plan</p>
          {plan ? (
            <p className="text-sm text-text-secondary mt-0.5">
              Selling{' '}
              <span className="text-text-primary font-medium">{label}</span>
              {plan.sell_year ? <> in <span className="text-text-primary font-medium">{plan.sell_year}</span></> : ''}
              {plan.net_proceeds_gbp ? <> — net proceeds <span className="text-emerald-light font-medium">{fmt(plan.net_proceeds_gbp)}</span></> : ''}
            </p>
          ) : (
            <p className="text-sm text-text-muted mt-0.5 italic">
              Not in the plan. FIRE / MC / drawdown won&apos;t model a lump injection from this property.
            </p>
          )}
        </div>
        {!editing && (
          <div className="flex gap-1 shrink-0">
            {plan && (
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="px-2 py-1 rounded text-text-muted hover:text-rose-light hover:bg-rose/10 text-xs flex items-center gap-1"
                aria-label="Remove sale plan"
              >
                <Trash2 size={11} aria-hidden /> Remove
              </button>
            )}
            <button
              type="button"
              onClick={startEdit}
              className="px-3 py-1 rounded bg-blue/10 text-blue-light hover:bg-blue/20 border border-blue/30 text-xs flex items-center gap-1"
            >
              {plan ? <><Pencil size={11} aria-hidden /> Edit plan</> : <><Plus size={11} aria-hidden /> Add sale plan</>}
            </button>
          </div>
        )}
      </div>
      {editing && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Sale year</span>
            <input
              type="number"
              min={2024}
              max={2050}
              value={year === '' ? '' : year}
              onChange={(e) => setYear(e.target.value === '' ? '' : parseInt(e.target.value, 10) || '')}
              className="input mt-0.5 px-2 py-1.5 font-mono"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Net proceeds (£)</span>
            <input
              type="number"
              min={0}
              step={1000}
              value={proceeds}
              onChange={(e) => setProceeds(parseFloat(e.target.value) || 0)}
              className="input mt-0.5 px-2 py-1.5 font-mono"
            />
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className="btn-secondary px-3 py-1 text-xs"
            >
              <X size={11} aria-hidden /> Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn-primary px-3 py-1 text-xs"
            >
              <Save size={11} aria-hidden /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="sm:col-span-2 text-[10px] text-text-muted leading-tight">
            Saving updates household planning assumptions. The change flows
            into FIRE projection, Monte Carlo, drawdown sequence, and scenario
            stacker automatically.
          </p>
        </div>
      )}
    </div>
  )
}
